import React, { useState } from 'react';
import { Menu, Avatar, Typography, Divider, Space, Button, Tag } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    DashboardOutlined,
    AppstoreOutlined,
    HistoryOutlined,
    TagsOutlined,
    UserOutlined,
    SettingOutlined,
    LogoutOutlined,
    BellOutlined,
    BarChartOutlined,
    SwapOutlined,
    RollbackOutlined,
    MailOutlined
} from '@ant-design/icons';
import { clearOriginalAdminUser, loginSuccess } from '../../redux/userSlice';
import UserBadges from '../Common/UserBadges';
import env from '../../env';

const { Text } = Typography;

const SideMenu = ({ menuItems, defaultOpenKeys = [] }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { currentUser, token, originalAdminUser } = useSelector((state) => state.user);
    const [openKeys, setOpenKeys] = useState(() => {
        // Initialize with default open keys and any previously opened menus
        const storedOpenKeys = localStorage.getItem('menuOpenKeys');
        return storedOpenKeys ? JSON.parse(storedOpenKeys) : defaultOpenKeys;
    });

    const handleSwitchBackToAdmin = async () => {
        try {
            // Restore the original admin user
            dispatch(loginSuccess(originalAdminUser));
            dispatch(clearOriginalAdminUser());
            navigate('/admin/users');
        } catch (error) {
            console.error('Error switching back to admin:', error);
        }
    };

    // Get the current path to determine which menu item should be selected
    const getSelectedKeys = () => {
        const path = location.pathname;
        // For favorite pages
        if (path === '/favorite-auctions') return ['favorite-auctions'];
        if (path === '/favorite-lots') return ['favorite-lots'];
        // For invitation pages
        if (path === '/invitations') return ['pending-invitations'];
        if (path === '/invitations/accepted') return ['accepted-invitations'];
        if (path === '/invitations/sent') return ['sent-invitations'];
        // For other pages, return the main menu key
        return [path.replace('/', '')];
    };

    // Transform menuItems to include onClick handlers and add analytics
    const transformedMenuItems = [
        ...menuItems,
        {
            key: '/analytics',
            icon: <HistoryOutlined />,
            label: 'Bid History',
            onClick: () => navigate('/analytics')
        }
    ].map(item => ({
        ...item,
        onClick: item.label.props?.to ? () => navigate(item.label.props.to) : item.onClick,
        children: item.children?.map(child => ({
            ...child,
            onClick: child.label.props?.to ? () => navigate(child.label.props.to) : child.onClick
        }))
    }));

    const handleOpenChange = (keys) => {
        setOpenKeys(keys);
        // Store the open keys in localStorage
        localStorage.setItem('menuOpenKeys', JSON.stringify(keys));
    };

    return (
        <div style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            background: '#fff',
            borderRight: '1px solid #f0f0f0'
        }}>
            {/* User Info Section */}
            <div style={{ 
                padding: '24px 16px',
                textAlign: 'center',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Avatar 
                        size={64} 
                        icon={<UserOutlined />}
                        src={currentUser?.profile_image 
                            ? `${env.BACKEND_STORAGE_URL}/${currentUser.profile_image}` 
                            : currentUser?.profile_image_url || null
                        }
                        style={{ 
                            backgroundColor: '#1890ff',
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/profile')}
                    />
                    <Text strong style={{ fontSize: '16px' }}>
                        {currentUser?.first_name} {currentUser?.last_name}
                    </Text>
                    <div type="secondary" style={{ fontSize: '12px', marginTop: '-6px' }}>
                        {currentUser?.email}
                    </div>
                    {currentUser && (currentUser.is_verified || currentUser.seller_trust_badge || currentUser.buyer_protection_plus) && (
                        <div style={{ marginTop: '8px' }}>
                            <UserBadges user={currentUser} iconSize={14} />
                        </div>
                    )}
                    {originalAdminUser && (
                        <>
                            <Tag color="orange" style={{ width: '100%', fontSize: 12 }}>
                                Viewing: {currentUser?.first_name} {currentUser?.last_name}
                            </Tag>
                            <Button
                                type="primary"
                                icon={<RollbackOutlined />}
                                onClick={handleSwitchBackToAdmin}
                                style={{ marginTop: '8px' }}
                            >
                                Admin Panel
                            </Button>
                        </>
                    )}
                </Space>
            </div>

            {/* Main Menu */}
            <Menu
                mode="inline"
                selectedKeys={getSelectedKeys()}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                items={transformedMenuItems}
                style={{ 
                    flex: 1,
                    borderRight: 0,
                    padding: '8px 0'
                }}
            />
        </div>
    );
};

export default SideMenu; 